import { z } from 'zod';
import {
  clientSettingsSchema,
  contactPersonSchema,
  createModel,
  imageWithUploadSchema,
  legalEntitySchema,
  partyRoleSchema,
  partySchema,
  QueryMany,
  QueryOne,
  statusCommonSchema,
} from '../../common';
import { dateSchema, domainSchema, nameSchema } from '../../validation';
import { UTagSharedSchema } from '../utag';

/**
 * A CRM client's primary contact — the shared contact shape with every
 * field optional.
 *
 * Org / Carrier contacts keep `phone` and `name` required because those
 * are people the platform has to be able to reach (dispatch calls, driver
 * comms). A CRM contact is a note the Org keeps about someone else's
 * staff, and it arrives in pieces: a name from a chat message, a number
 * from a forwarded card, a job title rarely at all.
 *
 * Fully partial rather than "name required" because the inline-edit path
 * can genuinely produce any subset. `ClientService.updateClientById`
 * flattens `{ primaryContact: { phone } }` to a dotted `$set` and Mongo
 * validates nothing, so a `name`-required schema here would describe
 * documents the app is able to write anyway — a contract that lies is
 * worse than a permissive one.
 *
 * The stricter policy lives where it can actually be enforced:
 * `ClientUtilsService.getNewClientData` only builds a contact when a
 * person was named, so a phone with nobody attached never *starts* a
 * contact.
 */
export const clientContactSchema = contactPersonSchema.partial();
export type ClientContact = z.infer<typeof clientContactSchema>;

/**
 * `Client db model`
 *
 * A `Client` is a CRM entity owned by an `Org` representing any party
 * the Org transacts with: a customer, broker, carrier, or
 * individual. Unifies the legacy `Customer` collection (Phase 3
 * dual-write, Phase 6 backfill, final cutover in
 * `docs/relations-v2/05-future-cleanup-roadmap.md`).
 *
 * Optionally linked to a shared network identity via
 * {@link ClientSchema.linkedParty}. The link can point at either a
 * global `Profile` (Brand Directory entry, used pre-signup as a
 * deduplication anchor) or directly at a signed-up Hero `Org`. When
 * the link is a Profile and that Profile later acquires a
 * `linkedOrgId`, readers can bridge to the live Org without changing
 * the Client doc — `RouteDTOService.makeClientParty` stamps both
 * `profile_id` and `org_id` into `Party.ids[]` on write.
 */
export const ClientSchema = createModel({
  /** the status of the client */
  status: statusCommonSchema,
  /** the display name of the client. Such as Amazon, امازون. Not the full company name. */
  name: nameSchema,
  /**
   * Other names this client is known by — a trade name typed in chat, the
   * name printed on a card. Learned only when the user confirms it or
   * Bayan accepts a number under it; a match here skips the name judge.
   */
  aliases: z.array(nameSchema).optional(),

  /**
   * the network roles this client plays.
   *
   * A client can be a customer / carrier / broker / individual / contact —
   * multi-valued (sometimes a carrier orders from a broker and a broker
   * orders from a carrier). At least one role is required.
   */
  roles: z.array(partyRoleSchema).min(1),

  /**
   * the Org that owns this Client (the CRM-owning org).
   *
   * Carries a full {@link partySchema} snapshot — `_id`, `name`,
   * `target` (`'org'`), the org's network `roles[]`, and `ids[]`
   * (with the Org's `org_id` as the primary entry). Built by
   * {@link PartyResolverService.makeParty} on the writer side and by
   * the Phase 6 enrichment migration for already-mirrored docs.
   *
   * See `docs/relations-v2/02-data-model.md` §9.
   */
  org: partySchema,

  /**
   * The shared network identity this Client links to, if any.
   *
   * - `target: 'profile'` — linked to a Brand Directory entry, used
   *   pre-signup as a deduplication anchor. Bridges to a real Hero
   *   Org later via `Profile.linkedOrgId`. Set when an Org admin picks
   *   a brand from the unified picker (or "suggests" a new brand).
   * - `target: 'org'`     — linked directly to a signed-up Hero Org,
   *   skipping the Profile detour. Set when the picker finds an
   *   already-registered Org for the name the admin typed.
   * - `undefined`         — ad-hoc one-off Client with no shared
   *   identity (the "Skip" path on the picker).
   *
   * Generalizes the previously Profile-only `profile?` field. Same
   * {@link partySchema} shape as `org`; `ids[]` carries the primary
   * `profile_id` or `org_id` entry. Per ADR (relations-v2), a Client
   * may link directly to an Org when one already exists.
   */
  linkedParty: partySchema.optional(),

  /**
   * the primary contact of the client (default = the person who signed
   * up), when the Org knows one.
   *
   * **Optional, and its phone is optional too.** A Client is identified
   * by `name` + `roles`; everything else is enrichment. A hauling
   * company added so its CR can be declared on a government waybill has
   * no person attached at all, and a company someone names in passing
   * often comes with a person but no number yet. Requiring a reachable
   * human to record a company the Org demonstrably transacts with only
   * bought invented placeholder contacts.
   *
   * Readers must therefore treat both levels as absent-able —
   * `client.primaryContact?.phone` — rather than assuming a contact.
   */
  primaryContact: clientContactSchema.optional(),

  /** AI skills and instructions so that AI knows what to do with this client. */
  skill: nameSchema.optional(),

  /** client legal info */
  legal: legalEntitySchema.optional(),

  /**
   * the domain of the client such as amazon.com or tryhero.io.
   *
   * CRM-local — the Org's record of the client's domain. Independent of
   * any linked `Profile` (the Brand Directory entry has its own
   * `Profile.domain` field on the global record). Useful when:
   * - the Client has no Profile link (the Org typed in the CRM entry
   *   manually), or
   * - the Org wants to track a per-relationship domain that differs
   *   from the global brand domain.
   *
   * Readers that need "the domain for this brand" should prefer
   * `Profile.domain` when `client.linkedParty?.target === 'profile'`,
   * falling back to `client.domain`.
   */
  domain: domainSchema.optional(),

  /** the logo of the client. */
  logo: imageWithUploadSchema.optional(),

  /** the settings of the client */
  settings: clientSettingsSchema.optional(),

  /** a count of any member related to the client such as dispatchers / employees / managers */
  UTags: z.array(UTagSharedSchema),

  /** the last time the client shipped or received an order. */
  lastOrderDate: dateSchema.optional(),
});

/** `Client db model` */
export type Client = z.infer<typeof ClientSchema.model>;
export type ClientData = z.infer<typeof ClientSchema.data>;
export type ClientPartial = z.infer<typeof ClientSchema.partial>;
export type ClientQueryMany = QueryMany<Client>;
export type ClientQueryOne = QueryOne<Client>;
